import { useState, useCallback } from "react";

/**
 * Custom hook for managing text overlay state and operations
 * @param {React.RefObject} videoRef - Reference to the video element
 * @param {Function} clearHighlightSelection - Function to clear highlight selection
 * @returns {Object} Text overlay state and handler functions
 */
const useTextOverlays = (videoRef, clearHighlightSelection) => {
  const [textOverlays, setTextOverlays] = useState([]);
  const [selectedTextOverlay, setSelectedTextOverlay] = useState(null);

  const handleAddTextOverlay = useCallback(() => {
    if (!videoRef.current) return;

    const currentTime = videoRef.current.currentTime;

    const newTextOverlay = {
      id: Date.now(),
      startTime: currentTime,
      duration: 3,
      text: "New Text",
      fontSize: 24,
      color: "#ffffff",
      position: "center"
    };

    setTextOverlays(prev => [...prev, newTextOverlay]);
    setSelectedTextOverlay(newTextOverlay);
    if (clearHighlightSelection) {
      clearHighlightSelection();
    }
  }, [videoRef, clearHighlightSelection]);

  const handleSelectTextOverlay = useCallback((textOverlay) => {
    setSelectedTextOverlay(textOverlay);
    if (clearHighlightSelection) {
      clearHighlightSelection();
    }
  }, [clearHighlightSelection]);

  const handleUpdateTextOverlay = useCallback((field, value) => {
    setSelectedTextOverlay(currentSelected => {
      if (!currentSelected) return null;

      const updatedTextOverlay = { ...currentSelected, [field]: value };

      setTextOverlays(prev =>
        prev.map(t => t.id === currentSelected.id ? updatedTextOverlay : t)
      );

      return updatedTextOverlay;
    });
  }, []);

  const handleDeleteTextOverlay = useCallback((id) => {
    setTextOverlays(prev => prev.filter(t => t.id !== id));
    setSelectedTextOverlay(current => current?.id === id ? null : current);
  }, []);

  // Reset function for clearing all text overlay state
  const resetTextOverlays = useCallback(() => {
    setTextOverlays([]);
    setSelectedTextOverlay(null);
  }, []);

  return {
    textOverlays,
    setTextOverlays,
    selectedTextOverlay,
    setSelectedTextOverlay,
    handleAddTextOverlay,
    handleSelectTextOverlay,
    handleUpdateTextOverlay,
    handleDeleteTextOverlay,
    resetTextOverlays,
  };
};

export default useTextOverlays;
